import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { flowsKey, getFlows, getGraph, graphKey, type Flows, type Graph, type GraphEdge } from "./api";
import { caption, FLOW, layoutGraph, MAP_ERROR_TITLE, neighbours, NO_SCREENS_TEXT, pathThrough, SCREEN, tapLine } from "./format";
import { isNoRelease, JourneyHeader, LoadError, NoRelease, useJourneySearch } from "./parts";

// C6 Map view (dev spec 4070402f; UX design §3 F2, §4, §5). Every captured screen of the release as a
// node, edges labelled with the tap; select a node to read what comes before and after it.

const NODE_W = 132;
const NODE_H = 48;
const COL_GAP = 72;
const ROW_GAP = 20;

export function MapTab() {
  const { values, set } = useJourneySearch();
  const graphQuery = useQuery({ queryKey: graphKey, queryFn: getGraph, retry: false });
  const flowsQuery = useQuery({ queryKey: flowsKey, queryFn: getFlows, retry: false });
  const selected = typeof values[SCREEN] === "string" ? (values[SCREEN] as string) : null;

  if (graphQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        <JourneyHeader />
        {isNoRelease(graphQuery.error) ? (
          <NoRelease />
        ) : (
          <LoadError title={MAP_ERROR_TITLE} error={graphQuery.error} retry={() => void graphQuery.refetch()} />
        )}
      </div>
    );
  }
  const graph = graphQuery.data?.data;
  if (!graph) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <JourneyHeader />
        <p role="status" className="sr-only">
          Loading the map…
        </p>
        <Skeleton className="h-96 w-full rounded-ks-md" />
      </div>
    );
  }
  if (graph.nodes.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <JourneyHeader />
        <p role="status" className="text-sm text-ks-grey-1">
          {NO_SCREENS_TEXT}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <JourneyHeader />
      <div className="flex flex-col gap-4 min-[1100px]:flex-row">
        <GraphCanvas graph={graph} selected={selected} onSelect={(route) => set({ [SCREEN]: route })} />
        <SidePanel graph={graph} flows={flowsQuery.data?.data} flowsFailed={flowsQuery.isError} retryFlows={() => void flowsQuery.refetch()} route={selected} />
      </div>
    </div>
  );
}

function GraphCanvas({ graph, selected, onSelect }: { graph: Graph; selected: string | null; onSelect: (route: string) => void }) {
  const placed = layoutGraph(graph);
  const at = new Map(placed.map((p) => [p.route, p]));
  const x = (column: number) => column * (NODE_W + COL_GAP);
  const y = (row: number) => row * (NODE_H + ROW_GAP);
  const width = Math.max(...placed.map((p) => x(p.column))) + NODE_W;
  const height = Math.max(...placed.map((p) => y(p.row))) + NODE_H;
  // Reading order for keyboard focus: column, then row (UX §6 Map canvas).
  const ordered = [...placed].sort((a, b) => a.column - b.column || a.row - b.row);
  return (
    <div className="min-w-0 flex-1 overflow-auto rounded-ks-md border border-ks-grey-4 bg-ks-surface p-4">
      <div className="relative" style={{ width, height }}>
        <svg width={width} height={height} className="absolute inset-0" aria-hidden>
          {graph.edges.map((e) => {
            const from = at.get(e.from);
            const to = at.get(e.to);
            if (!from || !to || e.from === e.to) return null;
            const x1 = x(from.column) + NODE_W;
            const y1 = y(from.row) + NODE_H / 2;
            const x2 = x(to.column);
            const y2 = y(to.row) + NODE_H / 2;
            return (
              <g key={`${e.from}|${e.tap}|${e.to}`} className="text-ks-grey-3">
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" />
                <title>{tapLine(e.tap)}</title>
              </g>
            );
          })}
        </svg>
        {ordered.map((n) => (
          <button
            key={n.route}
            type="button"
            onClick={() => onSelect(n.route)}
            aria-pressed={selected === n.route}
            title={caption(n)}
            style={{ left: x(n.column), top: y(n.row), width: NODE_W, height: NODE_H }}
            className={`absolute flex flex-col justify-center overflow-hidden rounded-ks-md border px-2 text-left text-xs focus-visible:outline-2 focus-visible:outline-ks-purple ${
              selected === n.route ? "border-ks-purple bg-ks-purple-4 text-ks-ink" : "border-ks-grey-3 bg-ks-surface text-ks-ink"
            }`}
          >
            <span className={n.frame_id ? "font-medium" : "font-medium text-ks-warn"}>{n.frame_id ?? `⚠ ${n.page ?? n.route}`}</span>
            <span className="truncate text-ks-grey-1">{n.route}</span>
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-ks-grey-1">Click a screen to see what comes before and after it. ⚠ = no canvas frame.</p>
    </div>
  );
}

function SidePanel({
  graph,
  flows,
  flowsFailed,
  retryFlows,
  route,
}: {
  graph: Graph;
  flows: Flows | undefined;
  flowsFailed: boolean;
  retryFlows: () => void;
  route: string | null;
}) {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  if (!route) {
    return (
      <aside aria-live="polite" className="w-full text-sm text-ks-grey-1 min-[1100px]:w-80">
        Select a screen.
      </aside>
    );
  }
  const node = graph.nodes.find((n) => n.route === route);
  if (!node) {
    return (
      <aside aria-live="polite" className="w-full text-sm text-ks-grey-1 min-[1100px]:w-80">
        Not in this release.
      </aside>
    );
  }
  const { before, after } = neighbours(graph.edges, route);
  const label = (r: string) => {
    const n = graph.nodes.find((m) => m.route === r);
    return n ? caption(n) : r;
  };
  const pathName = pathThrough(graph.edges, route);
  const owner = pathName && flows ? findPath(flows, pathName) : null;
  const showSequence = () => {
    if (!owner) return;
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area ?? "experience_delivery", module: params.module ?? "app_journey", tab: "sequence" },
      search: Object.fromEntries([[`f_${FLOW}`, owner.flow], ...Object.entries(owner.vars).map(([k, v]) => [`f_${k}`, v])]) as never,
    });
  };
  return (
    <aside aria-live="polite" className="flex w-full flex-col gap-3 text-sm min-[1100px]:w-80">
      <div>
        <p className="font-medium text-ks-ink">{caption(node)}</p>
        <p className="text-xs text-ks-grey-1">{node.route}</p>
      </div>
      <EdgeList title="Before" edges={before} side="from" label={label} />
      <EdgeList title="After" edges={after} side="to" label={label} />
      {owner && (
        <Button variant="secondary" size="sm" onClick={showSequence}>
          Show in a sequence
        </Button>
      )}
      {flowsFailed && (
        <p className="text-xs text-ks-grey-1">
          Could not load the flows, so this screen cannot open in a sequence.{" "}
          <Button variant="ghost" size="xs" onClick={retryFlows}>
            Try again
          </Button>
        </p>
      )}
    </aside>
  );
}

function EdgeList({ title, edges, side, label }: { title: string; edges: GraphEdge[]; side: "from" | "to"; label: (route: string) => string }) {
  return (
    <section>
      <h3 className="font-medium text-ks-ink">{title}</h3>
      {edges.length === 0 ? (
        <p className="text-ks-grey-1">–</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {edges.map((e) => (
            <li key={`${e.from}|${e.tap}|${e.to}`} className="text-ks-ink">
              {label(e[side])} <span className="text-ks-grey-1">({tapLine(e.tap)})</span>
              <span className="block text-xs text-ks-grey-1">Proved by: {e.paths.join(", ")}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function findPath(flows: Flows, name: string): { flow: string; vars: Record<string, string> } | null {
  for (const [flow, def] of Object.entries(flows.flows)) {
    const path = def.captured.find((p) => p.name === name);
    if (path) return { flow, vars: path.vars };
  }
  return null;
}
